import React, { useState } from 'react';

/**
 * "כפתור שיתוף" block — lets a visitor pass the page on to someone else.
 *
 * One block type, N channels (WhatsApp / Facebook / Telegram / X / email /
 * SMS / copy-link / native share sheet). The visitor picks a channel, we open
 * that network's share intent with the CURRENT page URL.
 *
 * Shared by the builder canvas (BlockRenderer), the builder preview
 * (PreviewModal → BlockRenderer) and both public renderers (PublicCampaign,
 * PublicTool) so all four render identically.
 *
 * The URL is always taken from the live page (`shareUrl`), never authored.
 * That matters for ambassadors: an ambassador landing on `?ref=ABC` shares a
 * link that still carries their ref, so the donations they bring keep being
 * attributed to them.
 */

export type ShareChannel =
  | 'whatsapp' | 'facebook' | 'telegram' | 'x' | 'email' | 'sms' | 'copy' | 'native';

export const SHARE_CHANNELS: ShareChannel[] = [
  'whatsapp', 'facebook', 'telegram', 'x', 'email', 'sms', 'copy', 'native',
];

export interface ShareBlockProps {
  /** Ordered list of enabled channels. */
  channels?: ShareChannel[];
  /** 'buttons' = brand-coloured pill + label · 'icons' = round icon only · 'outline' = white pill with brand icon */
  variant?: 'buttons' | 'icons' | 'outline';
  /** Optional heading above the buttons, e.g. "שתפו עם חברים". */
  title?: string;
  /** Colour of that heading. */
  titleColor?: string;
  /**
   * Message that goes out with the link. `{title}` = page/campaign name,
   * `{url}` = the page URL. When `{url}` isn't written explicitly, the link
   * is appended at the end (WhatsApp/Telegram/SMS need it inside the text).
   */
  shareText?: string;
  /** Show the channel name next to the icon (ignored by the 'icons' variant). */
  showLabels?: boolean;
  /** Icon diameter (px) for 'icons', icon glyph size for the pill variants. */
  iconSize?: number;
  /** Space between buttons (px). */
  gap?: number;
  align?: 'flex-start' | 'center' | 'flex-end';
  /** Off = every button uses `bgColor`/`textColor` instead of the network's brand colour. */
  useBrandColors?: boolean;
  bgColor?: string;
  textColor?: string;
  borderRadius?: number;
  /** Stretch the buttons to fill the block width (one per row). */
  fullWidth?: boolean;
}

type Lang = 'he' | 'en';

const LABELS: Record<Lang, Record<ShareChannel, string>> = {
  he: {
    whatsapp: 'וואטסאפ', facebook: 'פייסבוק', telegram: 'טלגרם', x: 'X',
    email: 'אימייל', sms: 'SMS', copy: 'העתקת קישור', native: 'שיתוף',
  },
  en: {
    whatsapp: 'WhatsApp', facebook: 'Facebook', telegram: 'Telegram', x: 'X',
    email: 'Email', sms: 'SMS', copy: 'Copy link', native: 'Share',
  },
};

const COPIED_LABEL: Record<Lang, string> = { he: 'הקישור הועתק!', en: 'Link copied!' };

const BRAND: Record<ShareChannel, string> = {
  whatsapp: '#25D366',
  facebook: '#1877F2',
  telegram: '#229ED9',
  x: '#000000',
  email: '#EA4335',
  sms: '#16a34a',
  copy: '#64748b',
  native: '#2563eb',
};

export const SHARE_CHANNEL_META: { key: ShareChannel; label: string; color: string }[] =
  SHARE_CHANNELS.map(key => ({ key, label: LABELS.he[key], color: BRAND[key] }));

/* ── Icons (inline so nothing depends on an icon font loading) ────────────── */

const ICONS: Record<ShareChannel, React.ReactNode> = {
  whatsapp: <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />,
  facebook: <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />,
  telegram: <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />,
  x: <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />,
  email: <path d="M1.5 5.25A2.25 2.25 0 0 1 3.75 3h16.5a2.25 2.25 0 0 1 2.25 2.25v13.5A2.25 2.25 0 0 1 20.25 21H3.75a2.25 2.25 0 0 1-2.25-2.25zm2.4.75 8.1 5.4 8.1-5.4zm16.2 2.55-7.53 5.02a1.2 1.2 0 0 1-1.34 0L3.7 8.55V18.6h16.4z" />,
  sms: <path d="M12 2C6.477 2 2 5.94 2 10.8c0 2.77 1.46 5.24 3.75 6.86-.13 1.2-.6 2.55-1.6 3.72-.2.24-.02.6.29.56 2.2-.3 3.86-1.2 4.9-1.96.85.2 1.75.3 2.66.3 5.523 0 10-3.94 10-8.8S17.523 2 12 2zM7.5 12a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5zm4.5 0a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5zm4.5 0a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5z" />,
  copy: <path d="M9.53 13.53a.75.75 0 0 1 0-1.06l2.94-2.94a3.75 3.75 0 1 1 5.3 5.3l-1.77 1.77a.75.75 0 1 1-1.06-1.06l1.77-1.77a2.25 2.25 0 0 0-3.18-3.18l-2.94 2.94a.75.75 0 0 1-1.06 0zm4.94-3.06a.75.75 0 0 1 0 1.06l-2.94 2.94a3.75 3.75 0 1 1-5.3-5.3l1.77-1.77a.75.75 0 1 1 1.06 1.06L7.29 10.23a2.25 2.25 0 0 0 3.18 3.18l2.94-2.94a.75.75 0 0 1 1.06 0z" />,
  native: <path d="M18 16.08a2.9 2.9 0 0 0-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81a3 3 0 1 0-3-3c0 .24.04.47.09.7L8.04 9.81A3 3 0 1 0 6 15c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65a2.92 2.92 0 1 0 2.92-2.92z" />,
};

/* ── Share-intent builders ───────────────────────────────────────────────── */

/**
 * Fill `{title}` / `{url}`.
 *
 * `appendUrl` is for the channels that carry ONE free-text field (WhatsApp,
 * SMS, email body): the link has to live inside the message or it's lost, so
 * it's appended unless the author placed `{url}` themselves. Channels that
 * take the URL as its own parameter (Telegram, X, the native share sheet)
 * pass false — appending there would show the link twice.
 */
export function buildShareMessage(
  template: string | undefined,
  title: string,
  url: string,
  appendUrl = true,
): string {
  const raw = (template && template.trim()) || '{title}';
  const hadUrl = raw.includes('{url}');
  const text = raw.replace(/\{title\}/g, title).replace(/\{url\}/g, url).trim();
  if (!appendUrl) return text;
  return hadUrl ? text : (text ? `${text} ${url}` : url);
}

/**
 * The href for a channel, or null for the channels handled in JS
 * (copy / native share sheet).
 *
 * `message` already carries the link; `bareMessage` is the same copy without
 * it, for the channels that take `url` separately.
 */
export function shareHref(
  channel: ShareChannel,
  title: string,
  url: string,
  message: string,
  bareMessage: string,
): string | null {
  const e = encodeURIComponent;
  switch (channel) {
    case 'whatsapp': return `https://wa.me/?text=${e(message)}`;
    // Facebook's sharer only accepts a URL — it pulls the copy from the
    // page's OG tags (set per campaign in ShareSettingsModal).
    case 'facebook': return `https://www.facebook.com/sharer/sharer.php?u=${e(url)}`;
    case 'telegram': return `https://t.me/share/url?url=${e(url)}&text=${e(bareMessage)}`;
    case 'x': return `https://twitter.com/intent/tweet?url=${e(url)}&text=${e(bareMessage)}`;
    case 'email': return `mailto:?subject=${e(title)}&body=${e(message)}`;
    // `?&body=` is the form both iOS and Android accept.
    case 'sms': return `sms:?&body=${e(message)}`;
    default: return null;
  }
}

/**
 * Synchronous clipboard write via a throwaway textarea. Deprecated but still
 * the only path that works without a permission prompt, and — being sync — the
 * only one that can run inside a click handler without losing user activation.
 * Returns whether the copy actually happened.
 */
function legacyCopy(text: string): boolean {
  if (typeof document === 'undefined' || !text) return false;
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');            // keeps the iOS keyboard closed
  ta.style.position = 'fixed';
  ta.style.top = '0';
  ta.style.opacity = '0';
  ta.style.pointerEvents = 'none';
  document.body.appendChild(ta);
  let ok = false;
  try {
    ta.select();
    ta.setSelectionRange(0, text.length);     // iOS ignores select() alone
    ok = document.execCommand('copy');
  } catch {
    ok = false;
  }
  document.body.removeChild(ta);
  return ok;
}

/* ── Component ───────────────────────────────────────────────────────────── */

export const SHARE_DEFAULT_CHANNELS: ShareChannel[] = ['whatsapp', 'facebook', 'copy'];

export const ShareBlock: React.FC<{
  props?: ShareBlockProps;
  /** The live page URL. Ignored while `interactive` is false. */
  shareUrl: string;
  /** Campaign / page name — fills `{title}` and the email subject. */
  shareTitle?: string;
  /** False on the builder canvas: buttons render but don't fire. */
  interactive?: boolean;
  /** Canvas zoom factor (builder only). */
  scale?: number;
  fontFamily?: string;
  lang?: Lang;
}> = ({ props = {}, shareUrl, shareTitle = '', interactive = true, scale = 1, fontFamily, lang = 'he' }) => {
  const [copied, setCopied] = useState(false);

  const channels = (Array.isArray(props.channels) && props.channels.length
    ? props.channels
    : SHARE_DEFAULT_CHANNELS
  ).filter(c => SHARE_CHANNELS.includes(c));

  const variant = props.variant || 'buttons';
  const showLabels = variant === 'icons' ? false : props.showLabels !== false;
  const iconSize = (props.iconSize || (variant === 'icons' ? 44 : 18)) * scale;
  const gap = (props.gap ?? 10) * scale;
  const radius = (props.borderRadius ?? (variant === 'icons' ? 999 : 10)) * scale;
  const useBrand = props.useBrandColors !== false;
  const fullWidth = !!props.fullWidth;
  const labels = LABELS[lang];

  const message = buildShareMessage(props.shareText, shareTitle, shareUrl);
  const bareMessage = buildShareMessage(props.shareText, shareTitle, shareUrl, false);

  const flash = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copyLink = () => {
    // The legacy selection copy runs FIRST because it is synchronous and so
    // still holds the click's user activation. The async Clipboard API is
    // denied outright in several in-app browsers (Instagram, some Android
    // webviews) — and by the time its promise rejects the activation is gone,
    // which would leave the fallback unable to copy either. Trying the cheap
    // synchronous path first means the button always does something.
    if (legacyCopy(shareUrl)) { flash(); return; }
    navigator.clipboard?.writeText(shareUrl).then(flash).catch(() => { /* nothing left to try */ });
  };

  const handleClick = (channel: ShareChannel) => (e: React.MouseEvent) => {
    if (!interactive) { e.preventDefault(); return; }
    if (channel === 'copy') { e.preventDefault(); copyLink(); return; }
    if (channel === 'native') {
      e.preventDefault();
      const nav = navigator as any;
      // No native share sheet (most desktops) → copying the link is the
      // closest useful thing, and the "הועתק" flash tells the user it worked.
      if (typeof nav.share !== 'function') { copyLink(); return; }
      // `url` is its own field here, so the text must not repeat it.
      nav.share({ title: shareTitle || undefined, text: bareMessage, url: shareUrl }).catch(() => { /* user dismissed */ });
    }
  };

  const renderButton = (channel: ShareChannel) => {
    const brand = BRAND[channel];
    const label = labels[channel];
    const href = interactive ? shareHref(channel, shareTitle, shareUrl, message, bareMessage) : null;
    const isCopyish = channel === 'copy' || channel === 'native';
    const showCopied = copied && isCopyish;

    const bg = variant === 'outline'
      ? (props.bgColor || '#ffffff')
      : (useBrand ? brand : (props.bgColor || '#2563eb'));
    const fg = variant === 'outline'
      ? (useBrand ? brand : (props.textColor || '#111827'))
      : (props.textColor || '#ffffff');

    const common: React.CSSProperties = {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: showLabels ? 8 * scale : 0,
      background: bg,
      color: fg,
      border: variant === 'outline' ? `${Math.max(1, 1.5 * scale)}px solid ${useBrand ? brand : (props.textColor || '#d1d5db')}` : 'none',
      borderRadius: radius,
      textDecoration: 'none',
      cursor: interactive ? 'pointer' : 'default',
      fontFamily,
      fontWeight: 700,
      fontSize: `${14 * scale}px`,
      lineHeight: 1.2,
      whiteSpace: 'nowrap',
      boxSizing: 'border-box',
      transition: 'transform .15s ease, filter .15s ease',
      ...(variant === 'icons'
        ? { width: iconSize, height: iconSize, flexShrink: 0 }
        : { padding: `${10 * scale}px ${16 * scale}px`, minHeight: 40 * scale, flex: fullWidth ? '1 1 100%' : '0 0 auto' }),
    };

    const glyph = (
      <svg
        viewBox="0 0 24 24"
        fill="currentColor"
        width={variant === 'icons' ? '52%' : iconSize}
        height={variant === 'icons' ? '52%' : iconSize}
        style={{ flexShrink: 0 }}
        aria-hidden="true"
      >
        {ICONS[channel]}
      </svg>
    );

    const body = (
      <>
        {glyph}
        {showLabels && <span>{showCopied ? COPIED_LABEL[lang] : label}</span>}
      </>
    );

    // Real links stay <a> so long-press / open-in-new-tab keep working;
    // copy + native have no URL and must be buttons.
    if (href) {
      return (
        <a
          key={channel}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={label}
          title={label}
          style={common}
        >
          {body}
        </a>
      );
    }
    return (
      <button
        key={channel}
        type="button"
        onClick={handleClick(channel)}
        aria-label={label}
        title={label}
        style={common}
      >
        {body}
      </button>
    );
  };

  return (
    <div style={{
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: props.align === 'flex-start' ? 'flex-start' : props.align === 'flex-end' ? 'flex-end' : 'center',
      gap: 8 * scale,
      fontFamily,
    }}>
      {props.title ? (
        <div style={{
          fontSize: `${15 * scale}px`, fontWeight: 700,
          color: props.textColor && variant === 'outline' ? props.textColor : (props.titleColor || '#374151'),
          textAlign: 'center',
        }}>
          {props.title}
        </div>
      ) : null}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap,
        justifyContent: props.align || 'center',
        alignItems: 'center',
        width: '100%',
      }}>
        {channels.map(renderButton)}
      </div>
      {/* Icon-only variants have no room for the "copied" label — show it below. */}
      {copied && !showLabels && (
        <div style={{ fontSize: `${12 * scale}px`, fontWeight: 700, color: '#16a34a' }}>
          {COPIED_LABEL[lang]}
        </div>
      )}
    </div>
  );
};
